import { useState, useEffect } from 'react';
import '../App.css';
import styles from '../assets/styles/settings.module.css'
import { getOctopusConfig, setOctopusConfig } from '../lib/octopus';
import { usbSupported, getUsbFolder, chooseUsbFolder, forgetUsbFolder } from '../lib/usb';
import Toast from '../lib/toast';
import { useTheme } from "../lib/ThemeContext";
import { useAnimation, animationLevels } from "../lib/AnimationContext";
import Backarrow from '../components/Backarrow';
import { useDeviceInfo,downloadToHomescreen } from '../lib/mobile';
import {createDocument, useAuth } from "../lib/firebase";
import { themes, themeConfig, masterConfig, gamemodes } from '../configs/generalConfig';
import { playClickSound } from '../lib/audio';
import { copyDocument, getAdminEmails, isAdmin } from '../lib/firebase';
import { checkForAppUpdate } from '../lib/appUpdate';
import { OFFLINE_COPY } from '../lib/offline';

function SettingsPage({currentTab, setCurrentTab,eventPath,tbaEventOverride,setTbaEventOverride,eventList,refresh,funnySounds,setFunnySounds,sfxEnabled,setSfxEnabled,online,setSettingsYear,settingsYear, gamemode, setGamemode, setRebuiltMissType, rebuiltMissType, specialUpload, setSpecialUpload}) {
    const {device, isTouch} = useDeviceInfo();
    const { user, authentication } = useAuth();
    const [devmode, setDevmode] = useState(false);
    const [devmode2, setDevmode2] = useState(false);
    const [adminEmails, setAdminEmails] = useState([]);
    const { themeId, setThemeId, applyCustomTheme } = useTheme();
    const { level: animationLevel, setLevel: setAnimationLevel } = useAnimation();
    const [customTheme, setCustomTheme] = useState(() => {
        const tempMap = new Map();
        themeConfig.forEach(element => {
            tempMap.set(element, "");
        });
        return tempMap;
    });

    const [tbaInputValue, setTbaInputValue] = useState(tbaEventOverride || "");

    const [octopusServer, setOctopusServer] = useState(getOctopusConfig().server);
    const [usbFolderName, setUsbFolderName] = useState(null);

    useEffect(() => {
        getUsbFolder().then(handle => setUsbFolderName(handle ? handle.name : null));
    }, []);

    async function pickUsbFolder() {
        try {
            const name = await chooseUsbFolder();
            if (name) setUsbFolderName(name);
        } catch (err) {
            Toast.error("Couldn't use that folder: " + (err && err.message ? err.message : err));
        }
    }

    const [tbaSuggestions, setTbaSuggestions] = useState([]);
    const [showTbaSuggestions, setShowTbaSuggestions] = useState(false);

    useEffect(() => {
        setTbaInputValue(tbaEventOverride || "");
    }, [tbaEventOverride]);

    /**
     * Filters eventList for suggestions as the user types. Unlike the main event path input,
     * anything typed here is accepted even if it doesn't match a suggestion.
     */
    function handleTbaInputChange(e) {
        const val = e.target.value;
        setTbaInputValue(val);
        if (val.length > 0 && eventList) {
            const search = val.toLowerCase();
            const results = Array.from(eventList.entries())
                .filter(([key, name]) => key.toLowerCase().includes(search) || name.toLowerCase().includes(search))
                .map(([key, name]) => ({ key, name }));
            setTbaSuggestions(results);
            setShowTbaSuggestions(results.length > 0);
        } else {
            setTbaSuggestions([]);
            setShowTbaSuggestions(false);
        }
    }

    /**
     * Commits the override (triggering the TBA lookup) and forces an immediate refresh, since
     * committing the same value again wouldn't otherwise re-trigger the fetch effect.
     */
    function commitTbaOverride(rawValue) {
        const cleaned = String(rawValue || "").toLowerCase().replace(/\s/g, "");
        setTbaInputValue(cleaned);
        setShowTbaSuggestions(false);
        if (cleaned === (tbaEventOverride || "")) {
            // Same value as before: changing the override wouldn't re-trigger the fetch, so force one.
            refresh();
        } else {
            setTbaEventOverride(cleaned);
        }
    }

    function handleTbaBlur() {
        commitTbaOverride(tbaInputValue);
    }

    function handleTbaKeyDown(e) {
        if (e.key === "Enter") {
            e.target.blur();
        } else if (e.key === "Escape") {
            setTbaInputValue(tbaEventOverride || "");
            setShowTbaSuggestions(false);
            e.target.blur();
        }
    }

    function handleCustomThemeChange(tag, value){
        let tempMap = new Map();
        if(customTheme === null){
            themeConfig.forEach(element => {
                tempMap.set(element, "");
            });
        }
        else tempMap = new Map(customTheme);
        tempMap.set(tag, value);
        setCustomTheme(tempMap);
    }

    function SubmitCustomTheme(){
        if(customTheme === null) {
            Toast.error("Input fields are blank");
            return;
        }
        let go = true
        for (const [key, value] of customTheme) {
            if(!(/^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/.test(value))){
                Toast.error("" + key + " is not a valid hexcode");
                go = false;
                return;
            }
        };
        if(go === false) return;
        let pirate = Array.from(customTheme.values());
        applyCustomTheme(pirate);
    }

    function toggle(setter,getter,name) {
        setter(!getter);
        localStorage.setItem(name, String(!getter));
    }
    /**
     * Updates the admin email list from firebase whever authentication status changes
     */
    useEffect(() => {
        const fetchAdminEmails = async () => {
            let tempList = [];
            const result = await getAdminEmails(authentication);
            if (typeof result !== "boolean") {
                tempList = [...result];
            }
            setAdminEmails([...tempList]);
        };
        fetchAdminEmails();
    }, [authentication]);

    /**
     * Updates the visibilty of Dev Mode
     */

    useEffect(() => {
        isAdmin(authentication).then(isAdmin => {
            setDevmode(isAdmin);
        });
    }, [authentication, setDevmode]);

    /**
     * Checks for service worker updates and sends in the request to reload the page 
     * connected to the confirmation thingy in index.js
     * @returns 
     */
    function unregisterWorker() {
        if (!online) {
            Toast.error("No internet connection. Cannot Check for Updates.", funnySounds, sfxEnabled);
            return false;
        }
        if (OFFLINE_COPY) {
            Toast.info("This is the offline copy of Osprey. Download a new one from the website for updates.");
            return;
        }
        // Compares the running build with the deployed one and reloads past the service worker (lib/appUpdate.js)
        checkForAppUpdate().then(result => {
            if (result === "updating") Toast.info("Updating Osprey…");
            else if (result === "current") Toast.info("Already up to date!");
            else Toast.error("Couldn't check for updates. Try again, or press Ctrl+Shift+R.", funnySounds, sfxEnabled);
        });
    }

    function adminFunctionChecks(){
        const verify = window.confirm("This function is for 3128 Strategy Scouting Systems Team Members only. If not a member please press cancel.");
        if(verify === false) return false;
        if(authentication === false){
            Toast.error("Not authorized to access firebase");
            return false;
        }
        if(!(adminEmails.includes(user.email)) || adminEmails.length === 0){
            console.log(adminEmails)
            console.log(user.email);
            Toast.error("Account does not have firebase admin access");
            Toast.error("Unbelievable, you tried to do it anyways. Even after I JUST said only 3128 Strategy Scouting Systems Members. tsk tsk.");
            return false;
        }
        return true;
    }

    /**
     * copies the config firebase file from one location to another, be careful with this
     */
    async function copyConfig(){
        if(adminFunctionChecks() === false) return false;
        const sourceDocRef = prompt("Enter the Source Doc Path", "Competitions/Years/2025/config")
        if(sourceDocRef === "" || sourceDocRef === null){
            Toast.error("Empty Source Input, Copying Cancelled")
            return false;
        }
        const targetDocRef = prompt("Enter the Target Doc Path", "Competitions/Years/2026/config")
        if(targetDocRef === "" || targetDocRef === null){
            Toast.error("Empty Target Input, Copying Cancelled")
            return false;
        }
        const outcome = window.confirm("Final Check, are you sure you wish to copy this document? If you screw up the database I will find you.")
        if (outcome === false) return false;
        else{
            try{
                const result = await copyDocument(sourceDocRef,targetDocRef);
                if(result){
                    Toast.success("Document successfuly copied");
                    return true;
                } else{
                    Toast.error("Document copy failed");
                    return false;
                }
            } catch (err){
                Toast.error("Document copy failed");
            }
        }
    }


    /**
     * creates an event path in firebase and auto moves over the relevent config file
     * @returns 
     */
    async function createEventPath(){
        if(adminFunctionChecks() === false) return false;
        const targetYear = prompt("Enter the target year");
        if(targetYear === "" || targetYear === null || targetYear.length !== 4){
            Toast.error("Invalid Target Year, Event Creation Cancelled")
            return false;
        }

        const eventCode = prompt("Enter event code (not the year part)");
        if(eventCode === "" || eventCode === null){
            Toast.error("Empty Event Code Input, Event Creation Cancelled")
            return false;
        }

        const eventName = prompt("What is the event called");
        if(eventName === "" || eventName === null){
            Toast.error("Empty Event Name Input, Event Creation Cancelled")
            return false;
        }

        const outcome = window.confirm("Final Check, are you sure you wish to create this event? This will also copy over a config file to this event.")
        if (outcome === false) return false;
        else{
            try{
                const result = await createDocument(targetYear, eventCode, eventName);
                if(result){
                    Toast.success("Event successfuly created");

                    const sourceDocReff = "Competitions/Years/" + targetYear + "/config";
                    const targetDocReff = "Competitions/Years/" + targetYear + "/" + eventCode + "/Final/config";
                    const xiao = await copyDocument(sourceDocReff,targetDocReff);
                    if(xiao){
                        Toast.success("Config Copy Successful")
                    }
                    else{
                        Toast.error("Config Copy Failed");
                    }
                    return xiao;
                    
                } else {
                    Toast.error("Event creation failed");
                    return false;
                }
            } catch (err){
                Toast.error("Event creation failed spectacularly");
            }
        }
    }

    if(currentTab == "settings"){
        return (
            <div className={styles.Settings}>
                <Backarrow setCurrentTab={setCurrentTab}/>
                <div>
                    <h2>Settings</h2>
                </div>
                <div className={styles.buttonSection}>
                    <button className="uiButton"onClick={() => downloadToHomescreen()}>Download Osprey</button>
                    <button className="uiButton" onClick={() => unregisterWorker()}>Check for Updates</button>
                </div>
                <div className={styles.themeSection}>
                    <div className={styles.sectionTitle}>
                        <span className={styles.subtitle1}>Themes</span>
                        <span className={styles.subtitle2}>Adjust the way the app looks</span>
                    </div>
                    <div className={styles.themeOptions}>
                        {Object.entries(themes).map(([name, info]) => (name.substring(0, 5).includes("style") ? (
                            <button key={name} 
                                className={styles.themeBtn}
                                style={{ backgroundColor: themeId == name ? "var(--subColor)" : "" }}
                                onClick={() => {setThemeId(name); playClickSound(funnySounds,sfxEnabled)}}
                                >{name.substring(5)}</button>
                        ) : (
                            <button key={name} className="invisible"></button>
                        )
                        ))}
                    </div>
                    <span className={styles.subtitle2}></span>
                    <span className={styles.subtitle2}></span>
                    <h3 className={styles.subtitle2}>Custom Theme</h3>
                    <div  className={styles.themeInput}>
                        {themeConfig.map((item, i) => (
                            <div key={i} className={styles.themeInputOption}>
                                <span>
                                    {item}
                                </span>
                                <input type="color" value={customTheme.get(item)} className={styles.colorInput} onChange={(e) => (handleCustomThemeChange(item,e.target.value))}>
                                </input>
                            </div>
                        ))}
                        <div className={styles.themeInputOption}>
                            <button className='uiButton' onClick={() => SubmitCustomTheme()}>Save Theme</button>
                        </div>
                    </div>
                    
                </div>

                <div className={styles.themeSection}>
                    <div className={styles.sectionTitle}>
                        <span className={styles.subtitle1}>Scouting Settings</span>
                        <span className={styles.subtitle2}>The Game You Want to Scout</span>
                    </div>
                    <div className={styles.themeOptions}>
                        {Object.keys(masterConfig).filter(year => Number(year) < 2050).map((year, index) => (
                            <div className={styles.touchingBox}>
                                <div className={styles.container}>
                                    <label className={styles.switch}>
                                        <input type="checkbox" checked={parseInt(settingsYear) === parseInt(year)} onChange={() => setSettingsYear(year)} />
                                        <span className={styles.slider}></span>
                                    </label>
                                    <span>{year}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                    <span className={styles.subtitle2}></span>
                    <div className={styles.sectionTitle}>
                        <span className={styles.subtitle2}>Upload Mode</span>
                    </div>
                    <div className={styles.themeOptions}>
                        {gamemodes.map((item, index) => (
                            <div className={styles.touchingBox}>
                                <div className={styles.container}>
                                    <label className={styles.switch}>
                                        <input type="checkbox" checked={parseInt(gamemode) === parseInt(index)} onChange={() => setGamemode(index)} />
                                        <span className={styles.slider}></span>
                                    </label>
                                    <span>{item}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className={styles.themeSection}>
                    <div className={styles.sectionTitle}>
                        <span className={styles.subtitle1}>Octopus Server</span>
                        <span className={styles.subtitle2}>The Pi on the scouting switch. Leave blank to find it by itself (pi.db-hub.me, or 10.42.0.1 when DNS can't find the name). Type off to upload only to Firebase</span>
                    </div>
                    <div className={styles.themeOptions}>
                        <input
                            className="textInput"
                            style={{ width: "100%" }}
                            type="text"
                            autoCapitalize="none"
                            autoCorrect="off"
                            autoComplete="off"
                            value={octopusServer}
                            onChange={(e) => { setOctopusServer(e.target.value); setOctopusConfig({ server: e.target.value }); }}
                            placeholder="blank = find the Pi automatically"
                        />
                    </div>
                </div>

                <div className={styles.themeSection}>
                    <div className={styles.sectionTitle}>
                        <span className={styles.subtitle1}>USB Folder</span>
                        <span className={styles.subtitle2}>{usbSupported()
                            ? "A folder on the flash drive. Submit and the search page's Upload / Export save this laptop's queue there whenever the drive is plugged in, for the Relay tab's Import Folder"
                            : "Needs Chrome or Edge"}</span>
                    </div>
                    {usbSupported() && (
                        <div className={styles.themeOptions}>
                            <span style={{ flex: 1 }}>{usbFolderName ? `Using: ${usbFolderName}` : "No folder chosen"}</span>
                            <button className="uiButton" onClick={pickUsbFolder}>{usbFolderName ? "Change" : "Choose"}</button>
                            {usbFolderName && (
                                <button className="uiButton" onClick={async () => { await forgetUsbFolder(); setUsbFolderName(null); }}>Remove</button>
                            )}
                        </div>
                    )}
                </div>

                <div className={styles.themeSection}>
                    <div className={styles.sectionTitle}>
                        <span className={styles.subtitle1}>TBA Event</span>
                        <span className={styles.subtitle2}>Overrides which TBA event teams/matches are pulled from, without changing where scouted data is saved</span>
                    </div>
                    <div className={styles.themeOptions}>
                        <div className={styles.tbaAutocompWrapper}>
                            <input
                                className="textInput"
                                style={{ width: "100%" }}
                                type="text"
                                autoCapitalize="none"
                                autoCorrect="off"
                                autoComplete="off"
                                value={tbaInputValue}
                                onChange={handleTbaInputChange}
                                onFocus={() => { if (tbaSuggestions.length > 0) setShowTbaSuggestions(true); }}
                                onBlur={handleTbaBlur}
                                onKeyDown={handleTbaKeyDown}
                                placeholder={eventPath ? `Default: ${eventPath}` : "e.g. 2026casnd"}
                            />
                            {showTbaSuggestions && (
                                <ul className={styles.tbaSuggestionList}>
                                    {tbaSuggestions.map(s => (
                                        <li
                                            key={s.key}
                                            onMouseDown={(e) => { e.preventDefault(); commitTbaOverride(s.key); }}
                                        >
                                            {s.key} - ({s.name})
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>

                <div className={styles.themeSection}>
                    <div className={styles.sectionTitle}>
                        <span className={styles.subtitle1}>{settingsYear} Special Settings</span>
                        <span className={styles.subtitle2}>Miss/Accuracy Input Type</span>
                    </div>
                    <div className={styles.themeOptions}>
                        {parseInt(settingsYear) === 2026 && (
                            <div className={styles.touchingBox}>
                                <div className={styles.container}>
                                    <label className={styles.switch}>
                                        <input type="checkbox" checked={specialUpload} onChange={() => toggle(setSpecialUpload,specialUpload,"specialUploadStatus")} />
                                        <span className={styles.slider}></span>
                                    </label>
                                    <span>Special Upload</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className={styles.themeSection}>
                    <div className={styles.sectionTitle}>
                        <span className={styles.subtitle1}>QOL Features</span>
                        <span className={styles.subtitle2}>They don't actually change much</span>
                    </div>
                    <div className={styles.themeOptions}>
                        <div className={styles.touchingBox}>
                            <div className={styles.container}>
                                <label className={styles.switch}>
                                    <input type="checkbox" checked={sfxEnabled} onChange={() => toggle(setSfxEnabled,sfxEnabled,"sfxStatus")} />
                                    <span className={styles.slider}></span>
                                </label>
                                <span>{sfxEnabled ? "SFX Enabled" : "SFX Disabled"}</span>
                            </div>
                        </div>
                        
                        <div className={styles.touchingBox}>
                            <div className={styles.container}>
                                <label className={styles.switch}>
                                    <input type="checkbox" checked={funnySounds} onChange={() => toggle(setFunnySounds,funnySounds,"funnyStatus")} />
                                    <span className={styles.slider}></span>
                                </label>
                                <span>{funnySounds ? "Funny Sounds Enabled" : "Funny Sounds Disabled"}</span>
                            </div>
                        </div>
                    </div>
                    <span className={styles.subtitle2}>Animations</span>
                    <div className={styles.themeOptions}>
                        {animationLevels.map((level) => (
                            <button key={level}
                                className={styles.themeBtn}
                                style={{ backgroundColor: animationLevel === level ? "var(--subColor)" : "" }}
                                onClick={() => {setAnimationLevel(level); playClickSound(funnySounds,sfxEnabled)}}
                                >{level.charAt(0).toUpperCase() + level.slice(1)}</button>
                        ))}
                    </div>
                </div>
                {devmode === true ? (
                    <div className={styles.devbox}>
                        <h2>Dev Mode Box</h2>
                        <p>Device: {device}</p>
                        <p>Touchable: {String(isTouch)}</p>
                        <p>Authentication: {String(authentication)}</p>
                        <button className="uiButton" onClick={() => unregisterWorker()}>Unregister SW</button>
                        <button className="uiButton" onClick={() => copyConfig()}>Copy Configs</button>
                        <button className="uiButton" onClick={() => createEventPath()}>create new event</button>
                    </div>
                ) : (
                    <div></div>
                )}
            </div>
        );
    }
}

export default SettingsPage;
